import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';

import { IngredientSection, SectionHint } from '../../../art-style/components/IngredientSection';
import { adLanguageLabel } from '../../ad-language';
import type { SiteLanguagesState } from '../../hooks/useSiteLanguages';
import { LumioMark } from '../LumioMark';
import { StudioSelect } from '../StoryboardComposer/StudioSelect';
import * as S from './AdLanguageSection.styles';

export interface AdLanguageSectionProps {
  /** The section's id (one per rail). */
  id: string;
  open: boolean;
  onToggle: () => void;
  /** The asset's language ('' on an older document: the website's default is shown in its place). */
  value: string;
  siteLanguages: SiteLanguagesState;
  /** The owner picked a language: a normal edit (it forks a locked version like any other ingredient). */
  onChange: (language: string) => void;
  disabled?: boolean;
  /** Where the subject's copy in this language stands (useLocalizedCopy). */
  copyStatus?: 'idle' | 'loading' | 'ready' | 'error';
  /** Some of the subject's copy was translated by Lumio (the website has none in this language). */
  translated?: boolean;
  onRetry?: () => void;
  /** What the language means for this asset, in place of "All text in this asset is written in this language." (a
   *  landing page opens in it and keeps the website's other languages). */
  hint?: React.ReactNode;
}

/**
 * The "Language" ingredient of an ad composer: which of the brand website's languages every word of the asset is
 * written in. The options are the website's languages (the default first), named in their own tongue (endonyms,
 * never translated). Under the picker it says how the subject's copy stands: being prepared, translated by Lumio, or
 * failed (with Try again). When the website's languages cannot be read the picker is disabled and says so.
 */
export const AdLanguageSection = ({
  id,
  open,
  onToggle,
  value,
  siteLanguages,
  onChange,
  disabled = false,
  copyStatus = 'idle',
  translated = false,
  onRetry,
  hint,
}: AdLanguageSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const unknown = siteLanguages.status === 'error' || (siteLanguages.status === 'ready' && !siteLanguages.known);
  const shownLabel = adLanguageLabel(value || siteLanguages.defaultLanguage);
  const options = React.useMemo(
    () => siteLanguages.languages.map((language) => ({ value: language, label: adLanguageLabel(language) })),
    [siteLanguages.languages],
  );

  return (
    <IngredientSection id={id} title={t('Language')} icon="globe" summary={shownLabel} open={open} onToggle={onToggle}>
      <StudioSelect
        label="Language"
        hideLabel
        rawLabels
        value={value}
        options={options}
        onChange={onChange}
        placeholder={shownLabel}
        disabled={disabled || unknown || siteLanguages.status !== 'ready'}
      />
      {unknown ? (
        <SectionHint text="The website's languages could not be read." tag="span" />
      ) : (
        hint ?? <SectionHint text="All text in this asset is written in this language." tag="span" />
      )}
      {copyStatus === 'loading' ? (
        <SectionHint text="Preparing the promotion's text in this language..." tag="span" />
      ) : null}
      {copyStatus === 'error' ? (
        <S.HintRow>
          <SectionHint text="The promotion's text could not be prepared in this language." tag="span" />
          {onRetry ? <Button variant="text" label={t('Try again')} onClick={onRetry} /> : null}
        </S.HintRow>
      ) : null}
      {translated && copyStatus !== 'loading' && copyStatus !== 'error' ? (
        <S.HintRow>
          <LumioMark size={14} />
          <SectionHint
            text="Lumio translated the promotion's text: the website has none in this language."
            tag="span"
          />
        </S.HintRow>
      ) : null}
    </IngredientSection>
  );
};

export default AdLanguageSection;
